import { inject, Injectable, NgZone, OnDestroy, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { RebarAuthService } from '../rebarauth/rebar.auth.service';

/**
 * Logs the user out after a fixed period of inactivity (no clicks, key presses,
 * mouse/touch movement, scrolling or form input). The activity listeners run
 * outside Angular's zone so frequent events (e.g. mousemove) don't trigger change
 * detection; the timer is reset on every interaction.
 */
@Injectable({ providedIn: 'root' })
export class IdleTimeoutService implements OnDestroy {
  /** Idle period before auto-logout. */
  private readonly idleTimeoutMs = 45 * 60 * 1000; // 20 minutes

  /** DOM events that count as user activity. */
  private readonly activityEvents = [
    'mousedown',
    'mousemove',
    'wheel',
    'keydown',
    'touchstart',
    'scroll',
    'click',
    'input',
  ];

  private readonly zone = inject(NgZone);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly rebarAuth = inject(RebarAuthService);

  private timerId: ReturnType<typeof setTimeout> | null = null;
  private started = false;
  private readonly onActivity = (): void => this.resetTimer();

  /** Begin monitoring. Safe to call multiple times — only the first call takes effect. */
  start(): void {
    if (this.started || !isPlatformBrowser(this.platformId)) return;
    this.started = true;

    // Register listeners outside Angular so high-frequency events don't run change detection.
    this.zone.runOutsideAngular(() => {
      this.activityEvents.forEach((event) =>
        window.addEventListener(event, this.onActivity, { passive: true })
      );
      this.resetTimer();
    });
  }

  /** Stop monitoring and remove all listeners. */
  stop(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.activityEvents.forEach((event) =>
      window.removeEventListener(event, this.onActivity)
    );
    this.clearTimer();
    this.started = false;
  }

  private resetTimer(): void {
    this.clearTimer();
    this.timerId = setTimeout(() => this.handleTimeout(), this.idleTimeoutMs);
  }

  private clearTimer(): void {
    if (this.timerId !== null) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }

  private handleTimeout(): void {
    this.stop();
    // Re-enter Angular's zone so the MSAL logout redirect / navigation runs correctly.
    this.zone.run(() => this.rebarAuth.logout());
  }

  ngOnDestroy(): void {
    this.stop();
  }
}
